import apiService from '../../../services/api-client';

export interface InventoryCountSession {
  id: number;
  name: string;
  warehouseId: number;
  warehouseName: string;
  status: 'draft' | 'active' | 'closed' | 'cancelled';
  source: 'auto' | 'manual_file' | 'integration';
  varianceThresholdPct: string;
  theoreticalItemsCount: number;
  theoreticalOperatorItemsCount: number;
  theoreticalClientItemsCount: number;
  createdAt: string;
  activatedAt: string | null;
  closedAt: string | null;
}

export interface TheoreticalLoadResult {
  sessionId: number;
  source: string;
  sourceType: 'operator' | 'client';
  loadedCount: number;
  createdProductsCount: number;
}

export interface TheoreticalUploadItem {
  sku: string;
  productName?: string;
  theoreticalQty: number;
  location?: string;
}

// 'operator' = teorico del propio sistema del operador logistico (ej.
// AGUNSA); 'client' = teorico del sistema del dueno de la carga. Un 3PL
// puede cargar los dos para la misma sesion y conciliar el fisico contra
// ambos por separado.
export type TheoreticalSourceType = 'operator' | 'client';

export interface Warehouse {
  id: number;
  name: string;
}

export interface InventoryCountItemSummary {
  productId: number;
  sku: string;
  barcode: string | null;
  productName: string;
  location: string | null;
  // "mejor disponible" (operador si existe, si no cliente) -- para pantallas
  // que solo necesitan UN numero de referencia (conteo manual, alerta).
  theoreticalQty: string;
  theoreticalOperatorQty: string | null;
  theoreticalClientQty: string | null;
  countedQty: string | null;
  isCounted: boolean;
  lastCountedAt: string | null;
  lastMethod: string | null;
}

export interface InventoryCountItemsListResponse {
  sessionId: number;
  total: number;
  counted: number;
  percent: number;
  varianceThresholdPct: string;
  items: InventoryCountItemSummary[];
}

export interface InventoryCountEntryResponse {
  id: number;
  sessionId: number;
  productId: number;
  countedQty: string;
  method: 'manual' | 'scanner' | 'camera';
  countedByUserId: number | null;
  countedAt: string;
}

export interface SessionParticipant {
  userId: number;
  fullName: string | null;
  email: string | null;
  entriesCount: number;
  lastActivityAt: string;
  isRecentlyActive: boolean;
}

export interface DashboardSessionSummary {
  id: number;
  name: string;
  warehouseName: string;
  status: 'draft' | 'active' | 'closed' | 'cancelled';
  total: number;
  counted: number;
  percent: number;
  skusWithVariance: number;
  activatedAt: string | null;
  participants: SessionParticipant[];
}

export interface DashboardResponse {
  sessions: DashboardSessionSummary[];
}

export interface ReconciliationItem {
  productId: number;
  sku: string;
  barcode: string | null;
  productName: string;
  location: string | null;
  theoreticalOperatorQty: string | null;
  theoreticalClientQty: string | null;
  countedQty: string | null;
  differenceOperatorQty: string | null;
  differenceOperatorPct: number | null;
  exceedsThresholdOperator: boolean;
  differenceClientQty: string | null;
  differenceClientPct: number | null;
  exceedsThresholdClient: boolean;
  isCounted: boolean;
  method: string | null;
  countedAt: string | null;
  countedByUserId: number | null;
  countedByName: string | null;
  countedByEmail: string | null;
}

export interface ReconciliationSummary {
  sessionId: number;
  sessionName: string;
  warehouseName: string;
  status: string;
  varianceThresholdPct: string;
  totalSkus: number;
  countedSkus: number;
  percent: number;
  totalTheoreticalOperatorQty: string;
  totalTheoreticalClientQty: string;
  totalCountedQty: string;
  totalDifferenceOperatorQty: string;
  totalDifferenceClientQty: string;
  skusWithVarianceOperator: number;
  skusWithVarianceClient: number;
  // true si esta sesion tiene teorico de cliente cargado -- el frontend usa
  // esto para ocultar las columnas de cliente cuando no aplica (la mayoria
  // de las empresas, que no son 3PL).
  hasClientTheoretical: boolean;
  generatedAt: string;
}

export interface ReconciliationResponse {
  summary: ReconciliationSummary;
  items: ReconciliationItem[];
}

export interface WebhookSendResult {
  sessionId: number;
  queued: boolean;
  message: string;
}

export interface WebhookConfig {
  webhookUrl: string | null;
  // Interruptor general del modulo para la empresa. Solo un superadmin
  // puede cambiarlo (ver updateWebhookConfig).
  inventoryCountEnabled: boolean;
}

export const inventoryCountService = {
  async listWarehouses(companyId: number) {
    const { data } = await apiService().get<Warehouse[]>(`/warehouses?company_id=${companyId}`);
    return data;
  },

  async listSessions(companyId: number) {
    const { data } = await apiService().get<InventoryCountSession[]>(`/inventory-count/${companyId}/sessions`);
    return data;
  },

  async createSession(
    companyId: number,
    payload: { name: string; warehouseId: number; varianceThresholdPct?: number }
  ) {
    const { data } = await apiService().post<InventoryCountSession>(`/inventory-count/${companyId}/sessions`, payload);
    return data;
  },

  async loadTheoreticalAuto(companyId: number, sessionId: number) {
    const { data } = await apiService().post<TheoreticalLoadResult>(
      `/inventory-count/${companyId}/sessions/${sessionId}/theoretical/auto`
    );
    return data;
  },

  async loadTheoreticalUpload(
    companyId: number,
    sessionId: number,
    items: TheoreticalUploadItem[],
    sourceType: TheoreticalSourceType = 'operator'
  ) {
    const { data } = await apiService().post<TheoreticalLoadResult>(
      `/inventory-count/${companyId}/sessions/${sessionId}/theoretical/upload`,
      { items, sourceType }
    );
    return data;
  },

  async activateSession(companyId: number, sessionId: number) {
    const { data } = await apiService().post<InventoryCountSession>(
      `/inventory-count/${companyId}/sessions/${sessionId}/activate`
    );
    return data;
  },

  async listActiveSessions(companyId: number) {
    const { data } = await apiService().get<InventoryCountSession[]>(
      `/inventory-count/${companyId}/active-sessions`
    );
    return data;
  },

  async listSessionItems(companyId: number, sessionId: number, statusFilter: 'all' | 'pending' | 'counted' = 'all') {
    const { data } = await apiService().get<InventoryCountItemsListResponse>(
      `/inventory-count/${companyId}/sessions/${sessionId}/items?status_filter=${statusFilter}`
    );
    return data;
  },

  async submitCount(
    companyId: number,
    sessionId: number,
    productId: number,
    payload: { countedQty: number; method: 'manual' | 'scanner' | 'camera' }
  ) {
    const { data } = await apiService().post<InventoryCountEntryResponse>(
      `/inventory-count/${companyId}/sessions/${sessionId}/items/${productId}/count`,
      payload
    );
    return data;
  },

  async getDashboard(companyId: number) {
    const { data } = await apiService().get<DashboardResponse>(`/inventory-count/${companyId}/dashboard`);
    return data;
  },

  async closeSession(companyId: number, sessionId: number) {
    const { data } = await apiService().post<InventoryCountSession>(
      `/inventory-count/${companyId}/sessions/${sessionId}/close`
    );
    return data;
  },

  // Solo permitido por el backend mientras la sesion siga en 'draft' (no
  // activada todavia) -- una sesion activa o cerrada se cierra, no se borra.
  async deleteSession(companyId: number, sessionId: number) {
    await apiService().delete(`/inventory-count/${companyId}/sessions/${sessionId}`);
  },

  async getReconciliation(companyId: number, sessionId: number) {
    const { data } = await apiService().get<ReconciliationResponse>(
      `/inventory-count/${companyId}/sessions/${sessionId}/reconciliation`
    );
    return data;
  },

  async downloadReconciliationExcel(companyId: number, sessionId: number) {
    const response = await apiService().get(
      `/inventory-count/${companyId}/sessions/${sessionId}/reconciliation/export`,
      { responseType: 'blob' }
    );
    const blob = response.data;
    if (blob.size === 0) throw new Error('El archivo descargado está vacío');

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'conciliacion.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match?.[1]) filename = match[1];
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  async sendReconciliationWebhook(companyId: number, sessionId: number) {
    const { data } = await apiService().post<WebhookSendResult>(
      `/inventory-count/${companyId}/sessions/${sessionId}/reconciliation/send-webhook`
    );
    return data;
  },

  async getWebhookConfig(companyId: number) {
    const { data } = await apiService().get<WebhookConfig>(`/inventory-count/${companyId}/webhook-config`);
    return data;
  },

  // inventoryCountEnabled undefined = no tocarlo (el backend rechaza el
  // cambio igual si no eres superadmin, esto solo evita mandar el campo
  // cuando esta pantalla la ve un company_admin).
  async updateWebhookConfig(companyId: number, webhookUrl: string | null, inventoryCountEnabled?: boolean) {
    const { data } = await apiService().put<WebhookConfig>(`/inventory-count/${companyId}/webhook-config`, {
      webhookUrl,
      inventoryCountEnabled,
    });
    return data;
  },
};
